/* eslint-disable @typescript-eslint/no-explicit-any */
import { Document as RichTextDocument } from '@contentful/rich-text-types';

import { CTAContent } from '@/components/Elements/CTA';
import { IconName } from '@/components/Icon/Icon';
import { ButtonContent } from '@/components/OldButton/OldButton';
import { SectionType } from '@/components/Section/Section';
import {
  BlogCategoryCf,
  BlogCategoryNavCf,
  DynamicRedirectPageCF,
  ExitIntentCF,
  GlobalFooterCf,
  GlobalNavCf,
  SectionCf,
} from '@/types';

import {
  BlogCategoryPageDynamicContent,
  BlogCategoryPageLinkedArticlesContentCF,
  BlogPageLinkedArticlesContentCF,
  EcommStoreProductPageEnhancedCF,
} from './api/types';

export enum HeaderSize {
  'H1' = 'H1',
  'H2' = 'H2',
  'H3' = 'H3',
  'H4' = 'H4',
  'H5' = 'H5',
  'H6' = 'H6',
}

// CONTENTFUL SYSTEM

export interface AnyContent extends Contentful {
  fields: {
    [key: string]: unknown;
  };
}

export interface CardContent extends Contentful {
  fields: {
    cta: CTAContent;
    image: FrontifyImageContent[];
    title: string;
    subtitle: string;
    description: string;
    richDescription?: RichTextDocument;
    cardImageAltText?: string;
  };
}

export interface CardGroupContent extends Contentful {
  fields: {
    cards: CardContent[];
    title: string;
  };
}

export interface ComponentSys {
  space: SubSys;
  type: string;
  id: string;
  contentType: SubSys;
  revision: number;
  createdAt: Date;
  updatedAt: Date;
  publishedAt?: Date;
  firstPublishedAt?: Date;
  publishedVersion?: number;
  environment: SubSys;
  locale: string;
}

export interface SubSys {
  sys: {
    id: string;
    linkType: string;
    type: string;
  };
}

// CONTENT MODELS

export interface Footer extends Contentful {
  fields: {
    categories: Menu[];
    cta: CTAContent;
    legalLinks: Menu;
    name: string;
    socialLinks: SocialMediaLink[];
    topText: string;
    copyrightText: string;
    textColor?: string;
  };
}

export interface FrontifyImageContent {
  created: string;
  ext: string;
  generic_url: string;
  height: number;
  id: number;
  preview_url: string;
  src: string;
  title: string;
  width: number;
  name?: string;
}

export enum HeaderVariant {
  'Light Overlay' = 'Light Overlay',
  'Dark Overlay' = 'Dark Overlay',
  'Primary Blue' = 'Primary Blue',
}

export interface HeaderContent extends Contentful {
  fields: {
    categories: Menu[];
    cta: CTAContent | ButtonContent;
    cta2: CTAContent;
    hamburger?: Menu;
    hamburgerCta?: CTAContent;
    mySunrunButton?: CTAContent;
    name: string;
    promoBanner?: RichText;
    telephone?: CTAContent;
    appearance: HeaderVariant;
    socialMediaLinks?: SocialMediaLink[];
  };
}

export interface GlobalNavContent extends Contentful {
  fields: {
    categories: DropdownMenu[];
    promoCta?: CTAContent | PromoStateConstraintsList;
    loginCta: CTAContent;
    primaryCta: CTAContent;
    secondaryCta: CTAContent;
    name: string;
    style: ContentStyle;
    immersive: boolean;
  };
}

export enum ContentStyle {
  OnDark = 'OnDark',
  OnLight = 'OnLight',
}

export enum TextAlignment {
  Left = 'Left',
  Center = 'Center',
  Right = 'Right',
}

export interface DropdownMenuItem extends Contentful {
  fields: FieldlessDropdownMenuItem;
}

export interface FieldlessDropdownMenuItem {
  pageTitle: string;
  pageLink: string;
  chipText?: string;
  subtext?: string;
}

export interface FieldlessDropdownMenu {
  links: FieldlessDropdownMenuItem[];
  name: string;
  featured: FieldlessFeatureCard;
}

export interface DropdownMenu extends Contentful {
  fields: {
    links: DropdownMenuItem[];
    name: string;
    featured: FeatureCard;
  };
}

interface FeatureCard extends Contentful {
  fields: FieldlessFeatureCard;
}

export interface FieldlessFeatureCard {
  image: FrontifyImageContent[];
  cardTitle: string;
  link: string;
  buttonLabel: string;
  style: ContentStyle;
}

// Old navigation
export interface Menu extends Contentful {
  fields: {
    links: CTAContent[];
    name: string;
  };
}

export interface RichText {
  content: unknown[];
  data: unknown;
  nodeType: string;
}

export interface Seo {
  fields: {
    enableSearchEngineIndexing?: boolean;
    metaText?: string;
    metaTitle?: string;
    metaKeywords?: string[];
    previewImage?: FrontifyImageContent[];
    canonicalUrl?: string;
    excludeFromSitemap?: boolean;
  };
}

export enum SocialSites {
  FACEBOOK = 'Facebook',
  LINKEDIN = 'LinkedIn',
  TWITTER = 'Twitter',
}

export interface SocialMediaLink extends Contentful {
  fields: {
    icon?: FrontifyImageContent[];
    url: string;
    title: string;
  };
}
export interface Input {
  fields: {
    errorMessage?: string;
    invalidMessage?: string;
    label: string;
    placeholderValue?: string;
  };
  metaData: {
    tags: [];
  };
  sys: ComponentSys;
}

export enum VerticalSpacing {
  Top = 'Top',
  Center = 'Center',
  Bottom = 'Bottom',
  Even = 'Even',
}

export interface ColumnContent extends Contentful {
  fields: {
    content: AnyContent[];
    verticalSpacing: VerticalSpacing;
  };
}

export enum BackgroundColor {
  'Transparent' = 'Transparent',
  'White' = 'White',
  'Black' = 'Black',
  'Off White' = 'Off White',
  'Soft Navy' = 'Soft Navy',
  'Night Gradient' = 'Night Gradient',
  'Yellow White Gradient' = 'Yellow White Gradient',
  'Blue White Gradient' = 'Blue White Gradient',
}

export enum ColumnSplit {
  'Even' = 'Even',
  '1/2' = '1/2',
  '2/1' = '2/1',
  '3/1' = '3/1',
}

export enum BackgroundCover {
  Contain = 'Contain',
  Cover = 'Cover',
}

export enum BackgroundAnchor {
  Top = 'Top',
  Bottom = 'Bottom',
  Right = 'Right',
  Left = 'Left',
}

export interface SectionContent {
  fields: {
    content?: ColumnContent[];
    backgroundColor?: BackgroundColor;
    backgroundImage?: FrontifyImageContent[];
    columnSplit?: ColumnSplit;
    backgroundCover?: BackgroundCover;
    backgroundAnchor?: BackgroundAnchor;
  };
  metaData: {
    tags: [];
  };
  sys: ComponentSys;
}

export enum Site {
  CMS_SUNRUN_COM = 'cms.sunrun.com',
  CMS2_SUNRUN_COM = 'cms2.sunrun.com',
}

export interface Contentful {
  sys: ComponentSys;
}

export interface LinkContent extends Contentful {
  fields: {
    text: string;
    url: string;
    videoUrl?: string;
  };
}

export interface IconNameContent extends Contentful {
  fields: {
    name: IconName;
  };
}

export interface StateConstraintsList extends Contentful {
  fields: {
    constraintName: string;

    default?: any;
    constraints: StateConstraint[];
  };
}

export interface StateConstraint extends Contentful {
  fields: {
    constraintStateName: string;
    zipCodeList?: string;
    constraintType: boolean;
    states?: string[];

    alternateContent?: any;
  };
}

export interface PromoStateConstraint extends StateConstraint {
  fields: {
    constraintStateName: string;
    zipCodeList?: string;
    constraintType: boolean;
    states?: string[];
    alternateContent?: CTAContent;
  };
}

export interface PromoStateConstraintsList extends StateConstraintsList {
  fields: {
    constraintName: string;
    zipCodeList?: string;
    default?: CTAContent;
    constraints: PromoStateConstraint[];
  };
}

export interface SectionStateConstraint extends StateConstraint {
  fields: {
    constraintStateName: string;
    zipCodeList?: string;
    constraintType: boolean;
    states?: string[];
    alternateContent: ColumnContent[];
  };
}

export interface SectionStateConstraintsList extends StateConstraintsList {
  fields: {
    constraintName: string;
    zipCodeList?: string;
    default: ColumnContent[];
    constraints: SectionStateConstraint[];
  };
}

export type FeaturedComponent<T> = T & { isFeatured?: boolean };

export enum PageContentType {
  STANDARD = 'standardPage',
  BLOG = 'blogPage',
  BLOG_CATEGORY = 'blogCategoryPage',
  DYNAMIC_REDIRECT = 'dynamicRedirectPage',
  ECOMM_STORE_PRODUCT = 'ecommStoreProductPage',
}
export interface FooterMenuContent extends Contentful {
  fields: {
    name?: string;
    links: LinkContent[];
  };
}

export interface SocialIconLinkContent extends Contentful {
  fields: {
    url: string;
    icon: IconNameContent;
  };
}

// section issue
export interface GlobalFooterContent extends Contentful {
  fields: {
    style: ContentStyle;
    t1Menu: FooterMenuContent;
    linkColumns: FooterMenuContent[];
    solarByStateMenu: FooterMenuContent;
    socialIconLinks: SocialIconLinkContent[];
    copyrightText: string;
    legalLinks: FooterMenuContent;
  };
}

export interface StandardPageContent extends Contentful {
  fields: {
    header: HeaderContent | GlobalNavContent;
    sections: SectionType[];
    footer: Footer | GlobalFooterContent;
  };
}

export interface BlogPageContent extends Contentful {
  fields: {
    path: string;
    body: RichTextDocument;
    blogTitle: string;
    blogSubtitle?: string;
    backButton?: LinkContent;
    heroImage: [FrontifyImageContent];
    heroImageAltText?: string;
    video?: string;
    author?: string;
    publicationDate: string;
    header: HeaderContent | GlobalNavContent;
    footer: Footer | GlobalFooterContent;
    sections: SectionType[];
    socialShare: SocialSites[];
    tags: string[];
    showTableOfContents: boolean;
    curatedArticlesListHeading?: string;
    linkedArticleContent?: BlogPageLinkedArticlesContentCF;
  };
}

export interface BlogCategoryPageContentType extends Contentful {
  fields: {
    contentTitle?: string;
    header?: GlobalNavCf;
    category?: BlogCategoryCf;
    categoryNav?: BlogCategoryNavCf;
    eyebrow?: string;
    heading?: string;
    subheading?: string;
    sections?: SectionCf[];
    footer?: GlobalFooterCf;
    linkedArticleContent?: BlogCategoryPageLinkedArticlesContentCF;
    dynamicBlogContent?: BlogCategoryPageDynamicContent;
  };
}

export interface PageContent extends Contentful {
  fields: {
    path: string;
    site: Site;
    seoFields: Seo;
    fullStoryEnabled: boolean;
    exitIntent?: ExitIntentCF;
    pageContent:
      | StandardPageContent
      | BlogPageContent
      | BlogCategoryPageContentType
      | DynamicRedirectPageCF
      | EcommStoreProductPageEnhancedCF;
  };
}
